import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ChevronLeft } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { TruncatedText } from "@/components/Interface/TruncatedText/TruncatedText";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { getIconForProductCategory } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/ProductCategoryIcon.tsx";
import { SimStatusChip } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/SimStatusChip";
import { SimCardDetail } from "@/services/model";

/**
 * Header of the subscription detail view: a way back to the list, the product
 * icon, the Abo name, and its status.
 *
 * The icon sits beside the name from the tablet breakpoint up and above it on
 * mobile, where the name needs the full width. Long aliases ellipsis with the
 * same HintTooltip as the Abos list (full name on hover).
 */
export const Header = ({ simDetail }: { simDetail: SimCardDetail }) => {
  const { t } = useTranslation(["portal", "translation"]);
  const navigate = useNavigate();

  return (
    // All spacing (horizontal inset, top offset, gap to the tabs) comes from
    // TabLayout's header slot — the header card sits flush at the top of
    // the content column, like the Documents and Account headers. The section
    // only carries the semantic landmark and full width.
    <section className="w-full">
      <CardContainer
        className="flex w-full flex-col gap-gutter rounded-lg p-gutter"
        variant="ivory"
      >
        <Button
          dataTestid="detail-view-back"
          variant="tertiary"
          ariaLabel={t("translation:Common.label.back")}
          icon={<ChevronLeft />}
          onClick={() => navigate(-1)}
        />
        <div className="flex w-full flex-col gap-gutter tablet:flex-row tablet:items-end">
          <div className="flex size-24 shrink-0 items-center justify-center self-end rounded-lg bg-surface-primary-inverse tablet:self-auto">
            <div className="size-14">
              {getIconForProductCategory(simDetail)}
            </div>
          </div>
          <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
            <TruncatedText
              as="h1"
              className="-mb-1 w-full pb-1 text-headline-xl font-semibold leading-xs text-foreground-primary"
              dataTestId="detail-view-title"
              text={simDetail.alias ?? ""}
            />
            <SimStatusChip
              status={simDetail.simStatus}
              numberPorting={simDetail.numberPorting}
              servicePending={simDetail.servicePending}
              simStatusReason={simDetail.simStatusReason}
            />
          </div>
        </div>
      </CardContainer>
    </section>
  );
};
